import React, { useMemo, useState } from 'react';
import type { AdminUserListItem } from '../../types/users';
import { initialsOf } from '../../utils/format';

/**
 * Scelta dei destinatari di una comunicazione mirata. Mostra prima gli
 * utenti approvati; gli altri restano selezionabili (vedranno il messaggio
 * quando avranno accesso al portale).
 */
interface Props {
    users: AdminUserListItem[];
    loading: boolean;
    selected: number[];
    onChange: (ids: number[]) => void;
}

const RecipientPicker: React.FC<Props> = ({ users, loading, selected, onChange }) => {
    const [query, setQuery] = useState('');
    const [onlyApproved, setOnlyApproved] = useState(true);
    const selectedSet = useMemo(() => new Set(selected), [selected]);

    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        return users
            .filter(u => !onlyApproved || u.approvalStatus === 'Approved' || selectedSet.has(u.id))
            .filter(u => !q || `${u.email} ${u.displayName ?? ''} ${u.companyName ?? ''}`.toLowerCase().includes(q))
            .sort((a, b) => Number(selectedSet.has(b.id)) - Number(selectedSet.has(a.id)) || a.email.localeCompare(b.email));
    }, [users, query, onlyApproved, selectedSet]);

    const toggle = (id: number) =>
        onChange(selectedSet.has(id) ? selected.filter(x => x !== id) : [...selected, id]);

    const allVisibleSelected = visible.length > 0 && visible.every(u => selectedSet.has(u.id));
    const toggleVisible = () => {
        if (allVisibleSelected) onChange(selected.filter(id => !visible.some(u => u.id === id)));
        else onChange(Array.from(new Set([...selected, ...visible.map(u => u.id)])));
    };

    return (
        <div className="recipient-picker">
            <div className="recipient-picker__bar">
                <div className="filter-bar__search flex-grow-1">
                    <i className="bi bi-search" aria-hidden="true" />
                    <input
                        type="search"
                        className="form-control form-control-sm"
                        placeholder="Cerca per email, nome o azienda…"
                        aria-label="Cerca destinatari"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                    />
                </div>
                <div className="form-check form-switch m-0 small text-nowrap">
                    <input className="form-check-input" type="checkbox" id="rp-approved"
                        checked={onlyApproved} onChange={e => setOnlyApproved(e.target.checked)} />
                    <label className="form-check-label" htmlFor="rp-approved">Solo approvati</label>
                </div>
            </div>
            <div className="recipient-picker__head">
                <label className="d-flex align-items-center gap-2 m-0">
                    <input type="checkbox" className="form-check-input m-0" checked={allVisibleSelected}
                        onChange={toggleVisible} disabled={visible.length === 0} />
                    <span>{allVisibleSelected ? 'Deseleziona' : 'Seleziona'} i {visible.length} visibili</span>
                </label>
                <strong>{selected.length} selezionati</strong>
            </div>
            <div className="recipient-picker__list" role="group" aria-label="Utenti">
                {loading && <div className="et-state">Caricamento utenti…</div>}
                {!loading && visible.length === 0 && <div className="et-state">Nessun utente trovato.</div>}
                {visible.map(u => (
                    <label key={u.id} className={`recipient-picker__row ${selectedSet.has(u.id) ? 'is-selected' : ''}`}>
                        <input type="checkbox" className="form-check-input m-0" checked={selectedSet.has(u.id)} onChange={() => toggle(u.id)} />
                        <span className="user-avatar is-sm">{initialsOf(u.email, u.displayName)}</span>
                        <span className="recipient-picker__text">
                            <strong>{u.displayName || u.email}</strong>
                            <small>{u.displayName ? u.email : ''}{u.companyName ? `${u.displayName ? ' · ' : ''}${u.companyName}` : ''}</small>
                        </span>
                        {u.approvalStatus !== 'Approved' && <span className="badge bg-warning">Non approvato</span>}
                    </label>
                ))}
            </div>
        </div>
    );
};

export default RecipientPicker;
